/**
 * MeasurementResult domain object.
 *
 * Immutable computed measurement value, produced by the computation engine.
 */

import type { MeasurementType, MeasurementCategory, MeasurementUnit } from './templates/types';
import type { IMeasurementResult, MeasurementContext } from './types';

export class MeasurementResult implements IMeasurementResult {
    constructor(
        /** Matches the measurement definition ID */
        readonly id: string,
        /** Measurement type for display/grouping */
        readonly type: MeasurementType,
        /** Category for sidebar grouping */
        readonly category: MeasurementCategory,
        /** Display name */
        readonly name: string,
        /** Short label for 3D/CPR overlays where space is limited */
        readonly shortLabel: string,
        /** Computed value */
        readonly value: number,
        /** Unit */
        readonly unit: MeasurementUnit,
        /** Navigation target when clicked in sidebar */
        readonly navigationNodeId: number | null,
        /** Whether any input to this measurement has been manually edited */
        readonly hasEditedInputs: boolean,
        /** Type-specific context */
        readonly context: MeasurementContext
    ) {
        Object.freeze(this);
    }

    /**
     * Create a new MeasurementResult with selected fields overridden.
     * Returns a new frozen instance — the original is unchanged.
     */
    with(
        overrides: Partial<
            Pick<
                IMeasurementResult,
                'name' | 'shortLabel' | 'value' | 'navigationNodeId' | 'hasEditedInputs' | 'context'
            >
        >
    ): MeasurementResult {
        return new MeasurementResult(
            this.id,
            this.type,
            this.category,
            overrides.name ?? this.name,
            overrides.shortLabel ?? this.shortLabel,
            overrides.value ?? this.value,
            this.unit,
            overrides.navigationNodeId !== undefined ? overrides.navigationNodeId : this.navigationNodeId,
            overrides.hasEditedInputs !== undefined ? overrides.hasEditedInputs : this.hasEditedInputs,
            overrides.context ?? this.context
        );
    }
}
